import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  getDocs,
  query,
  orderBy,
  deleteDoc,
  where
} from 'firebase/firestore';
import { db, shouldUseLocalStorage } from './firebase';
import config from './config';
import { Description, SurveyResponse, ResultData, SurveyQuestion, ChiSquareResult } from './types';
import { getZodiacSign, shuffleArray } from './utils/zodiac';
import { saveSurvey, resetSaveState } from './utils/saveSurvey';
import { cleanupResults } from './utils/cleanupResults';
import './app.css';
import { isDevelopment } from './utils/env';

// Import components
import Introduction from './components/Introduction';
import PersonalitySelection from './components/PersonalitySelection';
import AnalysisPage from './components/AnalysisPage';
import { RetroBackground } from './components/RetroBackground';
import PasswordProtection from './components/PasswordProtection';

// DO NOT USE THIS IMPORT IN ARTIFACT - FOR BUILDING ONLY
// When deploying to Firebase, uncomment this and remove the local import above
// These imports won't work in Claude's environment but are needed for the real app
/* 
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
*/

// Zodiac descriptions data
const realDescriptions: Description[] = [
  { id: "aries", name: "Aries", text: "Bold, adventurous, competitive, and spontaneous. Jumps in first and figures it out later.", isReal: true },
  { id: "taurus", name: "Taurus", text: "Grounded, loyal, indulgent, and steady. Loves comfort, good food, and deep relationships.", isReal: true },
  { id: "gemini", name: "Gemini", text: "Curious, witty, changeable, and social. Great at conversation but can change opinions often.", isReal: true },
  { id: "cancer", name: "Cancer", text: "Emotional, nurturing, protective, and intuitive. Loyal but moody, like the tides.", isReal: true },
  { id: "leo", name: "Leo", text: "Confident, creative, dramatic, and proud. Loves to lead and be seen.", isReal: true },
  { id: "virgo", name: "Virgo", text: "Detail-oriented, practical, helpful, and perfectionistic. Loves lists, routines, and fixing things.", isReal: true },
  { id: "libra", name: "Libra", text: "Diplomatic, charming, indecisive, and beauty-loving. Seeks harmony and avoids conflict.", isReal: true },
  { id: "scorpio", name: "Scorpio", text: "Intense, passionate, secretive, and strategic. Feels deeply and trusts selectively.", isReal: true },
  { id: "sagittarius", name: "Sagittarius", text: "Optimistic, independent, restless, and philosophical. Loves freedom and exploration.", isReal: true },
  { id: "capricorn", name: "Capricorn", text: "Responsible, ambitious, self-disciplined, and traditional. Big on goals and hard work.", isReal: true },
  { id: "aquarius", name: "Aquarius", text: "Innovative, quirky, idealistic, and aloof. Marches to their own beat.", isReal: true },
  { id: "pisces", name: "Pisces", text: "Dreamy, empathetic, artistic, and sensitive. Deeply intuitive and often lost in imagination.", isReal: true }
];

const fakeDescriptions: Description[] = [
  { id: "ophiuchus", name: "Ophiuchus", text: "A rare blend of mystery and movement. Easily bored, yet loyal when inspired.", isReal: false },
  { id: "vulpecula", name: "Vulpecula", text: "Cunning, playful, always planning three steps ahead. Loves puzzles and chaos.", isReal: false },
  { id: "hydrus", name: "Hydrus", text: "Quiet but magnetic. Finds power in solitude and often surprises others with bursts of insight.", isReal: false },
];

// Survey questions
const surveyQuestions: SurveyQuestion[] = [
  {
    id: "beliefLevel",
    question: "Do you believe in astrology?",
    options: [
      "Yes, totally!",
      "I'm open to it but skeptical.",
      "Not really, but it's fun.",
      "Nope, it's complete nonsense."
    ]
  },
  {
    id: "knowledgeLevel",
    question: "How well do you know your astrological sign's personality traits?",
    options: [
      "I know them really well.",
      "I've heard a few things.",
      "I don't know much at all.",
      "I actively avoid them."
    ]
  },
  {
    id: "horoscopeReading",
    question: "Have you ever read your daily horoscope?",
    options: [
      "Yes, regularly",
      "Sometimes",
      "Rarely",
      "Never"
    ]
  },
  {
    id: "astrologicalInfluence",
    question: "Has astrology ever influenced a decision in your life (e.g., dating, timing, naming, career stuff)?",
    options: [
      "Yes",
      "Maybe subconsciously",
      "No",
      "Not that I'm aware of"
    ]
  }
];

// SurveyQuestions Component
const SurveyQuestions: React.FC<{
  questions: typeof surveyQuestions,
  responses: SurveyResponse,
  onAnswerChange: (questionId: string, answer: string) => void,
  onComplete: () => void
}> = ({ questions, responses, onAnswerChange, onComplete }) => {
  // Add scroll to top when SurveyQuestions component mounts
  useEffect(() => {
    console.log('=== SURVEY QUESTIONS MOUNTED ===');
    console.log('Initial responses:', responses);
    console.log('Storage type:', shouldUseLocalStorage() ? 'localStorage (Development)' : 'Firestore (Production)');
    window.scrollTo(0, 0);
  }, []); // Empty dependency array - only run on mount

  const allAnswered = questions.every(q => 
    responses[q.id as keyof SurveyResponse] !== ""
  );
  
  // Add state to track if save is in progress
  const [isSaving, setIsSaving] = useState(false);

  // Wrap onComplete to prevent multiple calls
  const handleComplete = async () => {
    console.log('=== HANDLE COMPLETE CALLED ===');
    console.log('Is saving:', isSaving);
    console.log('Survey responses:', responses);
    console.log('All questions answered:', allAnswered);
    console.log('Storage type:', shouldUseLocalStorage() ? 'localStorage (Development)' : 'Firestore (Production)');
    
    if (isSaving) {
      console.log('Preventing save - saving in progress');
      return;
    }

    setIsSaving(true);
    
    try {
      console.log('Calling onComplete');
      await onComplete();
      console.log('onComplete finished successfully');
    } catch (error) {
      console.error('Error in onComplete:', error);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="section survey-questions-section">
      <h2>Additional Questions</h2>
      <p className="survey-intro">These questions help us understand how people interact with astrology.</p>
      {questions.map((q) => (
        <div key={q.id} className="question-block">
          <h3>{q.question}</h3>
          <div className="options">
            {q.options.map((option) => (
              <label key={option} className="radio-label">
                <input 
                  type="radio" 
                  name={q.id} 
                  value={option}
                  checked={responses[q.id as keyof SurveyResponse] === option}
                  onChange={() => onAnswerChange(q.id, option)}
                  disabled={isSaving}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <button 
        onClick={handleComplete} 
        disabled={!allAnswered || isSaving}
        className="btn-primary"
      >
        {isSaving ? 'Saving...' : 'Complete Survey'}
      </button>
    </div>
  );
};

// Results Component
const Results: React.FC<{
  selectedId: string,
  allDescriptions: Description[],
  actualSign: string,
  onRestart: () => void,
  responses: SurveyResponse,
  toggleAnalysisMode: () => void
}> = ({ 
  selectedId, 
  allDescriptions, 
  actualSign, 
  onRestart,
  responses,
  toggleAnalysisMode
}) => {
  // Add scroll to top when Results component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selectedDesc = allDescriptions.find(d => d.id === selectedId);
  const actualDesc = realDescriptions.find(d => d.id === actualSign.toLowerCase());
  const isMatch = selectedId === actualSign.toLowerCase();
  
  // Get the actual sign's date range
  const getSignDateRange = (sign: string): string => {
    switch (sign.toLowerCase()) {
      case 'aries': return 'March 21 - April 19';
      case 'taurus': return 'April 20 - May 20';
      case 'gemini': return 'May 21 - June 20';
      case 'cancer': return 'June 21 - July 22';
      case 'leo': return 'July 23 - August 22';
      case 'virgo': return 'August 23 - September 22';
      case 'libra': return 'September 23 - October 22';
      case 'scorpio': return 'October 23 - November 21';
      case 'sagittarius': return 'November 22 - December 21';
      case 'capricorn': return 'December 22 - January 19';
      case 'aquarius': return 'January 20 - February 18';
      case 'pisces': return 'February 19 - March 20';
      default: return '';
    }
  };

  return (
    <div className="section results-section">
      <h2>
        <span className="spinning-star">⭐</span>
        Your Results
        <span className="spinning-star">⭐</span>
      </h2>

      <div className="birthday-display">
        <p>Birthday: {responses.birthday}</p>
      </div>
      
      <div className="result-card">
        <h3>Your Selection</h3>
        <p className="description">{selectedDesc?.text}</p>
        <div className="description-box">
          <p className="description-note">
            This is a {selectedDesc?.isReal ? 'real' : 'fake'} zodiac sign description.
          </p>
          <p className="zodiac-sign-name">{selectedDesc?.name}</p>
        </div>
      </div>
      
      <div className="result-card">
        <h3>Your Actual Zodiac Sign</h3>
        <p className="zodiac-sign-name">{actualDesc?.name}</p>
        <p className="date-range">Date Range: {getSignDateRange(actualSign)}</p>
        <div className="description-box">
          <p className="chosen-description">
            {actualDesc?.text}
          </p>
        </div>
      </div>
      
      <div className="result-card">
        <h3 className="rainbow-text">Did You Match?</h3>
        <p className={`match-result ${isMatch ? 'match' : 'no-match'}`}>
          {isMatch ? 'Yes! You picked your actual sign!' : 'No, you picked a different sign.'}
        </p>
        <p className="match-explanation">
          {isMatch 
            ? 'WOW! Your personality matched your zodiac sign\'s description!! Maybe astrologists are onto something'
            : 'Zero surprises here - astrology must be WHACK'}
        </p>
      </div>
      
      <div className="button-container">
        <button onClick={onRestart} className="btn-primary">
          Try Again
        </button>
        <button onClick={toggleAnalysisMode} className="btn-secondary">
          View Analysis
        </button>
      </div>
    </div>
  );
};

interface AppProps {
  initialMode?: 'analysis' | 'survey';
}

// Version check
const APP_VERSION = '2.0.0';

const App: React.FC<AppProps> = ({ initialMode = 'survey' }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentStep, setCurrentStep] = useState<'intro' | 'selection' | 'results' | 'survey'>('intro');
  const [selectedDescriptionId, setSelectedDescriptionId] = useState<string | null>(null);
  const [actualSign, setActualSign] = useState<string>('');
  const [descriptionsToShow, setDescriptionsToShow] = useState<Description[]>([]);
  const [surveyResponses, setSurveyResponses] = useState<SurveyResponse>(() => {
    console.log('Initializing surveyResponses state');
    return {
      selectedDescription: "",
      birthday: "",
      beliefLevel: "",
      knowledgeLevel: "",
      horoscopeReading: "",
      astrologicalInfluence: ""
    };
  });
  const [isAnalysisMode, setIsAnalysisMode] = useState(initialMode === 'analysis');
  const [resultsData, setResultsData] = useState<ResultData[]>([]);
  const [chiSquareResults, setChiSquareResults] = useState<ChiSquareResult | null>(null);
  const [saveInProgress, setSaveInProgress] = useState(false);

  // Add a ref to track if we've already saved
  const hasSavedRef = React.useRef(false);

  // Add logging refs
  const isDescriptionSelectionRef = React.useRef(false);
  const lastActionRef = React.useRef<string>('');

  // Add scroll to top on initial load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Clear any cached data on initial mount
  useEffect(() => {
    // Only clear data in development mode
    if (shouldUseLocalStorage()) {
      console.log('Clearing cached data on mount (development mode)');
      localStorage.removeItem('participantBirthday');
      localStorage.removeItem('participantName');
    }
  }, []); // Empty dependency array - only run once on mount

  // Add logging to useEffect
  useEffect(() => {
    console.log('=== COMPONENT RENDER ===');
    console.log('Current step:', currentStep);
    console.log('Last action:', lastActionRef.current);
    console.log('Is description selection:', isDescriptionSelectionRef.current);
    console.log('Survey responses:', surveyResponses);
  });

  // Initialize random descriptions on component mount
  useEffect(() => {
    console.log('Initializing descriptions. isAnalysisMode:', isAnalysisMode);
    if (!isAnalysisMode) {
      const birthday = localStorage.getItem('participantBirthday');
      console.log('Initializing with birthday:', birthday);
      
      if (birthday) {
        const actualSign = getZodiacSign(birthday).toLowerCase();
        console.log('Calculated sign:', actualSign);
        
        // Get the user's actual sign description
        const actualSignDescription = realDescriptions.find(d => d.id.toLowerCase() === actualSign);
        console.log('Found description:', actualSignDescription);
        
        if (!actualSignDescription) {
          console.error('Could not find description for sign:', actualSign);
          // Fallback to random selection
          const shuffledReal = shuffleArray(realDescriptions).slice(0, 3);
          const combined = shuffleArray([...shuffledReal, ...fakeDescriptions]);
          setDescriptionsToShow(combined);
          setActualSign(actualSign);
          updateSurveyResponse({
            birthday: birthday,
            beliefLevel: "",
            knowledgeLevel: "",
            horoscopeReading: "",
            astrologicalInfluence: ""
          }, 'initEffect');
          return;
        }
        
        // Filter out the user's actual sign from the real descriptions
        const otherRealDescriptions = realDescriptions.filter(d => d.id.toLowerCase() !== actualSign);
        
        // Select 2 random real descriptions from the remaining ones
        const shuffledOtherReal = shuffleArray(otherRealDescriptions).slice(0, 2);
        
        // Combine user's sign, 2 random real signs, and all fake signs
        const combined = shuffleArray([
          actualSignDescription,
          ...shuffledOtherReal,
          ...fakeDescriptions
        ]);
        
        console.log('Final descriptions:', combined);
        setDescriptionsToShow(combined);
        setActualSign(actualSign);
        updateSurveyResponse({
          birthday: birthday,
          beliefLevel: "",
          knowledgeLevel: "",
          horoscopeReading: "",
          astrologicalInfluence: ""
        }, 'initEffect');
      } else {
        console.log('No birthday found, using random selection');
        // Fallback to random selection if no birthday is set
        const shuffledReal = shuffleArray(realDescriptions).slice(0, 3);
        const combined = shuffleArray([...shuffledReal, ...fakeDescriptions]);
        setDescriptionsToShow(combined);
        setActualSign(combined[0].id);
        updateSurveyResponse({
          birthday: "",
          beliefLevel: "",
          knowledgeLevel: "",
          horoscopeReading: "",
          astrologicalInfluence: ""
        }, 'initEffect');
      }
    }
  }, [isAnalysisMode]); // Remove surveyResponses.birthday dependency

  // Update surveyResponses when birthday changes in localStorage
  useEffect(() => {
    console.log('Setting up storage event listener');
    const handleStorageChange = () => {
      const storedBirthday = localStorage.getItem('participantBirthday');
      console.log('Storage change detected. New birthday:', storedBirthday);
      if (storedBirthday && storedBirthday !== surveyResponses.birthday) {
        updateSurveyResponse({ birthday: storedBirthday }, 'storageEvent');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []); // Only run on mount and cleanup

  useEffect(() => {
    // Check for existing authentication
    const authExpiry = localStorage.getItem('authExpiry');
    if (authExpiry && new Date().getTime() < parseInt(authExpiry)) {
      setIsAuthenticated(true);
    }
  }, []);

  // Update analysis mode when initialMode prop changes
  useEffect(() => {
    setIsAnalysisMode(initialMode === 'analysis');
  }, [initialMode]);
  
  // Load results data when analysis mode is active
  useEffect(() => {
    if (isAnalysisMode) {
      fetchResultsData();
    }
  }, [isAnalysisMode]);
  
  // Fetch results from Firestore
  const fetchResultsData = async () => {
    try {
      // In development, use localStorage
      if (shouldUseLocalStorage()) {
        const savedData = localStorage.getItem('zodiacResultsData');
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            setResultsData(parsedData);
          } catch (e) {
            console.error('Error parsing saved data:', e);
          }
        }
        return;
      }

      // In production, use Firebase
      if (db) {
        const resultsRef = collection(db, config.app.resultsCollection);
        const resultsQuery = query(resultsRef, orderBy('timestamp', 'desc'));
        const querySnapshot = await getDocs(resultsQuery);
        
        const fetchedResults: ResultData[] = [];
        querySnapshot.forEach((doc) => {
          fetchedResults.push({ 
            id: doc.id, 
            ...doc.data() 
          } as ResultData);
        });
        
        if (fetchedResults.length > 0) {
          setResultsData(fetchedResults);
          // Only update localStorage in development mode
          if (shouldUseLocalStorage()) {
            localStorage.setItem('zodiacResultsData', JSON.stringify(fetchedResults));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };
  
  // Single function to handle all survey response updates
  const updateSurveyResponse = (updates: Partial<SurveyResponse>, source: string) => {
    console.log(`=== SURVEY RESPONSE UPDATE FROM ${source} ===`);
    console.log('Current responses:', surveyResponses);
    console.log('Updates:', updates);
    
    setSurveyResponses(prev => {
      const newResponses = { ...prev, ...updates };
      console.log('New responses:', newResponses);
      return newResponses;
    });
  };

  // Handle selection of personality description
  const handleDescriptionSelect = (id: string) => {
    console.log('Description selected:', id);
    
    // Find the description object
    const description = [...realDescriptions, ...fakeDescriptions].find(d => d.id === id);
    if (!description) {
      console.error('Description not found:', id);
      return;
    }
    
    console.log('Selected description details:', description);
    const isMatch = description.id === actualSign;
    console.log('Is match with actual sign:', isMatch);
    
    // Update both the selected ID and survey responses
    setSelectedDescriptionId(id);
    setSurveyResponses(prev => ({
      ...prev,
      selectedDescription: description.id,
      isMatch,
      isComplete: false
    }));
  };

  // Handle survey question responses
  const handleSurveyResponse = (questionId: string, answer: string) => {
    console.log('=== HANDLE SURVEY RESPONSE CALLED ===');
    console.log('Question ID:', questionId);
    console.log('Answer:', answer);
    
    // Only update state, no save
    setSurveyResponses(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  // Handle survey completion
  const handleSurveyComplete = async () => {
    console.log('=== HANDLE SURVEY COMPLETE CALLED ===');
    console.log('Current step:', currentStep);
    console.log('Selected description:', surveyResponses.selectedDescription);
    console.log('Survey responses:', surveyResponses);
    console.log('Storage type:', shouldUseLocalStorage() ? 'localStorage (Development)' : 'Firestore (Production)');
    console.log('Environment:', isDevelopment() ? 'Development' : 'Production');
    console.log('Firebase initialized:', !!db);
    console.log('Firebase initialized:', !!db);

    // Only save if we're in the survey step and have a selected description
    if (currentStep !== 'survey' || !surveyResponses.selectedDescription) {
      console.log('Skipping save - not in survey step or no description selected');
      return;
    }

    // Mark the survey as complete
    setSurveyResponses(prev => ({
      ...prev,
      isComplete: true
    }));

    try {
      console.log('Attempting to save survey to:', shouldUseLocalStorage() ? 'localStorage' : 'Firestore');
      const success = await saveSurvey(surveyResponses, realDescriptions, fakeDescriptions);
      console.log('Save result:', success ? 'Success' : 'Failed');
      
      if (success) {
        console.log('Moving to results step...');
        setCurrentStep('results');
      } else {
        console.log('Save failed, not moving to results step');
        // Reset the complete flag if save fails
        setSurveyResponses(prev => ({
          ...prev,
          isComplete: false
        }));
      }
    } catch (error) {
      console.error('Error saving survey:', error);
      // Reset the complete flag if save fails
      setSurveyResponses(prev => ({
        ...prev,
        isComplete: false
      }));
    }
  };
  
  // Clear all results data
  const clearAllResults = async () => {
    console.log('Starting clearAllResults function...');
    
    try {
      // Check if we should use Firebase
      if (!db || shouldUseLocalStorage()) {
        console.warn('Clearing localStorage only (development mode)');
        localStorage.removeItem('zodiacResultsData');
        localStorage.removeItem('zodiacAuthenticated');
        localStorage.removeItem('participantBirthday');
        localStorage.removeItem('participantName');
        setResultsData([]);
        return;
      }

      const collectionName = config.app.resultsCollection;
      console.log('Clearing Firestore collection:', collectionName);
      
      const resultsRef = collection(db, collectionName);
      const querySnapshot = await getDocs(resultsRef);
      
      if (querySnapshot.empty) {
        console.log('No documents to delete');
        return;
      }

      console.log(`Found ${querySnapshot.size} documents to delete`);
      
      // Delete each document
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log('All documents deleted successfully');
      
      // Only clear localStorage in development mode
      if (shouldUseLocalStorage()) {
        localStorage.removeItem('zodiacResultsData');
        localStorage.removeItem('zodiacAuthenticated');
        localStorage.removeItem('participantBirthday');
        localStorage.removeItem('participantName');
      }
      
      setResultsData([]);
    } catch (error) {
      console.error('Error deleting results:', error);
      throw error;
    }
  };

  // Toggle between survey and analysis mode
  const toggleAnalysisMode = async () => {
    console.log('=== TOGGLE ANALYSIS MODE ===');
    console.log('Current mode:', isAnalysisMode ? 'Analysis' : 'Survey');
    console.log('Auth required:', config.auth.requirePassword);
    console.log('Using localStorage:', shouldUseLocalStorage());
    
    // Only require authentication in production mode
    if (!isAnalysisMode && config.auth.requirePassword && !isDevelopment() && !isAuthenticated) {
      console.log('Waiting for authentication before toggling...');
      // Don't toggle yet, wait for authentication
      return;
    }
    
    // Only clean up results if we're entering analysis mode and haven't loaded data yet
    if (!isAnalysisMode && !resultsData.length) {
      console.log('Cleaning up results before entering analysis mode...');
      const cleanedResults = await cleanupResults();
      console.log('Cleanup complete, setting results data...');
      setResultsData(cleanedResults);
    }
    
    console.log('Toggling analysis mode...');
    setIsAnalysisMode(!isAnalysisMode);
    if (isAnalysisMode) {
      // Reset to step 1 when exiting analysis mode
      console.log('Resetting survey state...');
      resetSurveyState();
    }
    window.scrollTo(0, 0);
  };

  // Reset all survey-related state
  const resetSurveyState = () => {
    console.log('Resetting survey state');
    
    // Clear localStorage only in development mode
    if (shouldUseLocalStorage()) {
      localStorage.removeItem('participantBirthday');
      localStorage.removeItem('participantName');
    }
    
    // Reset all state
    setCurrentStep('intro');
    setSelectedDescriptionId(null);
    setActualSign('');
    updateSurveyResponse({
      selectedDescription: "",
      birthday: "",
      beliefLevel: "",
      knowledgeLevel: "",
      horoscopeReading: "",
      astrologicalInfluence: ""
    }, 'resetSurveyState');
    
    // Reset save state
    resetSaveState();
    
    // Re-shuffle descriptions
    const shuffledReal = shuffleArray(realDescriptions).slice(0, 3);
    const combined = shuffleArray([...shuffledReal, ...fakeDescriptions]);
    setDescriptionsToShow(combined);
    setActualSign(combined[0].id);
    
    window.scrollTo(0, 0);
  };

  // Restart survey
  const restartSurvey = () => {
    resetSurveyState();
  };

  // Calculate chi-square statistic
  const calculateChiSquare = (): ChiSquareResult | null => {
    if (resultsData.length === 0) return null;
    
    // Expected probability of a match is 1/6 (3 real out of 6 total descriptions, then 1/3 chance of it being your sign)
    const expectedMatchProbability = 1/6;
    const expectedMatches = resultsData.length * expectedMatchProbability;
    const expectedNonMatches = resultsData.length - expectedMatches;
    
    // Observed values
    const observedMatches = resultsData.filter(r => r.isMatch).length;
    const observedNonMatches = resultsData.length - observedMatches;
    
    // Chi-square calculation: Σ (observed - expected)² / expected
    const chiSquare = 
      Math.pow(observedMatches - expectedMatches, 2) / expectedMatches +
      Math.pow(observedNonMatches - expectedNonMatches, 2) / expectedNonMatches;
    
    // For a chi-square with 1 degree of freedom:
    // p < 0.05 threshold is 3.841
    // p < 0.01 threshold is 6.635
    const isSignificant = chiSquare > 3.841;
    
    // Calculate p-value
    const pValue = 1 - Math.exp(-chiSquare / 2);
    
    return {
      chiSquare,
      expectedMatches,
      observedMatches,
      isSignificant,
      degreesOfFreedom: 1,
      pValue
    };
  };

  // Update chiSquareResults when resultsData changes
  useEffect(() => {
    setChiSquareResults(calculateChiSquare());
  }, [resultsData]);

  // Add delete result function
  const handleDeleteResult = async (resultId: string) => {
    try {
      // Delete from Firebase if using it
      if (db && !shouldUseLocalStorage()) {
        const collectionName = config.app.resultsCollection;
        const q = query(collection(db, collectionName), where('id', '==', resultId));
        const querySnapshot = await getDocs(q);
        
        querySnapshot.forEach(async (doc) => {
          await deleteDoc(doc.ref);
        });
      }

      // Only update localStorage in development mode
      if (shouldUseLocalStorage()) {
        const existingData = JSON.parse(localStorage.getItem('zodiacResultsData') || '[]');
        const updatedData = existingData.filter((result: ResultData) => result.id !== resultId);
        localStorage.setItem('zodiacResultsData', JSON.stringify(updatedData));
      }

      // Update state
      setResultsData(prev => prev.filter(result => result.id !== resultId));
    } catch (error) {
      console.error('Error deleting result:', error);
      throw error;
    }
  };

  // Render current step
  const renderCurrentStep = () => {
    console.log('Rendering current step:', currentStep);
    
    // Only require authentication in production mode
    if (!isAuthenticated && !isDevelopment() && config.auth.requirePassword) {
      return <PasswordProtection onAuthenticated={() => setIsAuthenticated(true)} />;
    }

    if (isAnalysisMode) {
      return (
        <AnalysisPage
          resultsData={resultsData}
          clearAllResults={clearAllResults}
          toggleAnalysisMode={toggleAnalysisMode}
          chiSquareResults={chiSquareResults}
          setChiSquareResults={setChiSquareResults}
          realDescriptions={realDescriptions}
          fakeDescriptions={fakeDescriptions}
          onDeleteResult={handleDeleteResult}
        />
      );
    }

    switch (currentStep) {
      case 'intro':
        return <Introduction onNext={nextStep} toggleAnalysisMode={toggleAnalysisMode} />;
      case 'selection':
        console.log('Rendering selection step');
        return (
          <PersonalitySelection
            descriptions={descriptionsToShow}
            selected={selectedDescriptionId || ''}
            onSelect={handleDescriptionSelect}
            onNext={nextStep}
          />
        );
      case 'results':
        return (
          <Results
            selectedId={selectedDescriptionId || ''}
            allDescriptions={[...realDescriptions, ...fakeDescriptions]}
            actualSign={actualSign}
            onRestart={restartSurvey}
            responses={surveyResponses}
            toggleAnalysisMode={toggleAnalysisMode}
          />
        );
      case 'survey':
        return (
          <SurveyQuestions
            questions={surveyQuestions}
            responses={surveyResponses}
            onAnswerChange={handleSurveyResponse}
            onComplete={handleSurveyComplete}
          />
        );
      default:
        return <Introduction onNext={nextStep} toggleAnalysisMode={toggleAnalysisMode} />;
    }
  };

  // Next step
  const nextStep = () => {
    console.log('=== NEXT STEP CALLED ===');
    console.log('Current step:', currentStep);
    
    // Define the step sequence
    const stepSequence = ['intro', 'selection', 'survey', 'results'];
    const currentIndex = stepSequence.indexOf(currentStep);
    const nextStep = stepSequence[currentIndex + 1];
    
    console.log('Moving from', currentStep, 'to', nextStep);
    
    if (currentStep === 'intro') {
      // Get birthday from localStorage
      const birthday = localStorage.getItem('participantBirthday');
      if (birthday) {
        // Calculate actual sign
        const actualSign = getZodiacSign(birthday).toLowerCase();
        console.log('Calculated sign from birthday:', actualSign);
        
        // Get the user's actual sign description
        const actualSignDescription = realDescriptions.find(d => d.id.toLowerCase() === actualSign);
        if (actualSignDescription) {
          // Filter out the user's actual sign from the real descriptions
          const otherRealDescriptions = realDescriptions.filter(d => d.id.toLowerCase() !== actualSign);
          
          // Select 2 random real descriptions from the remaining ones
          const shuffledOtherReal = shuffleArray(otherRealDescriptions).slice(0, 2);
          
          // Combine user's sign, 2 random real signs, and all fake signs
          const combined = shuffleArray([
            actualSignDescription,
            ...shuffledOtherReal,
            ...fakeDescriptions
          ]);
          
          console.log('Setting descriptions with actual sign:', actualSign);
          setDescriptionsToShow(combined);
          setActualSign(actualSign);
        }
        
        // Only update state, no save
        setSurveyResponses(prev => ({
          ...prev,
          birthday
        }));
      }
    } else if (currentStep === 'selection') {
      // When moving from selection to survey, update the survey responses with the selected description
      if (selectedDescriptionId) {
        console.log('Updating survey responses with selected description:', selectedDescriptionId);
        // Only update state, no save
        setSurveyResponses(prev => ({
          ...prev,
          selectedDescription: selectedDescriptionId
        }));
      } else {
        console.log('No description selected, cannot proceed');
        return;
      }
    }
    
    // Only update step if we're not in survey (survey completion handles its own step change)
    if (currentStep !== 'survey') {
      console.log('Setting current step to:', nextStep);
      setCurrentStep(nextStep as 'intro' | 'selection' | 'results' | 'survey');
    }
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    // Check for version updates
    const checkVersion = async () => {
      try {
        // Fetch the version from a static file that gets updated with each deploy
        const response = await fetch('/version.json?t=' + new Date().getTime());
        const data = await response.json();
        
        if (data.version !== APP_VERSION) {
          console.log('New version available:', data.version, 'Current version:', APP_VERSION);
          // Clear cache and reload
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
              cacheNames.map(cacheName => caches.delete(cacheName))
            );
          }
          window.location.reload();
        }
      } catch (error) {
        console.error('Error checking version:', error);
      }
    };

    // Check version every 5 minutes
    checkVersion();
    const interval = setInterval(checkVersion, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="app-container">
      <RetroBackground />
      <div className="content-wrapper">
        {isAnalysisMode ? (
          <AnalysisPage 
            resultsData={resultsData}
            clearAllResults={clearAllResults}
            toggleAnalysisMode={toggleAnalysisMode}
            chiSquareResults={chiSquareResults}
            setChiSquareResults={setChiSquareResults}
            realDescriptions={realDescriptions}
            fakeDescriptions={fakeDescriptions}
            onDeleteResult={handleDeleteResult}
          />
        ) : (
          <>
            <header>
              <h1>{config.app.name}</h1>
              {!isAnalysisMode && (
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${(currentStep === 'survey' ? 0 : (currentStep === 'intro' ? 1 : (currentStep === 'selection' ? 2 : 3))/3) * 100}%` }}></div>
                </div>
              )}
            </header>
            <main>
              {renderCurrentStep()}
            </main>
            <footer>
              <div className="external-links">
                {/* Removed duplicate thedab.bar link */}
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default App;